import { useState } from "react";
import { Modal, Stack, Group, Title, Text, Textarea } from "@mantine/core";
import { useMutation } from "@apollo/client";
import { gql } from "@/__generated__";
import { Button } from "./buttons/button";
import { Toast } from "design-system/src/components/toast";

const CLOSE_APPEAL = gql(`
  mutation CloseAppeal($appealId: ID!, $comment: String!) {
    closeAppeal(appealId: $appealId, comment: $comment) {
      appealId
    }
  }
`);

interface CloseAppealModalProps {
  onClose: () => void;
  appealId: string;
  onSuccess: () => void;
}

export function CloseAppealModal({
  onClose,
  appealId,
  onSuccess,
}: CloseAppealModalProps) {
  const [reason, setReason] = useState("");

  const [closeAppeal, { loading: isLoading, error }] =
    useMutation(CLOSE_APPEAL);

  const handleSubmit = async () => {
    const { data, errors } = await closeAppeal({
      variables: {
        appealId,
        comment: reason,
      },
    });

    if (!errors && data) {
      onSuccess();
      handleClose();
    }
  };

  const handleClose = () => {
    setReason("");
    onClose();
  };

  const canSubmit = reason.trim().length > 0;

  return (
    <Modal
      opened={true}
      onClose={handleClose}
      title={<Title order={3}>Close Appeal</Title>}
      centered
      size="md"
    >
      <Stack gap="md">
        <Toast variant="error" isVisible={!!error}>
          {error?.message}
        </Toast>

        <Text size="sm" c="dimmed">
          Are you sure you want to close this appeal? This action cannot be
          undone.
        </Text>

        <Textarea
          label="Reason for closing"
          placeholder="Enter the reason for closing this appeal"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          minRows={3}
          required
        />

        <Group justify="flex-end" mt="md">
          <Button
            variant="secondary"
            size="medium"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="medium"
            onClick={handleSubmit}
            disabled={isLoading || !canSubmit}
            loading={isLoading}
          >
            {isLoading ? "Closing..." : "Close Appeal"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
